import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';

// ─── Tag Editor (Customer Types & Service Types) ───────────────────────────
function TagEditor({ label, description, items, onSave }) {
  const [list, setList] = useState(items);
  const [newItem, setNewItem] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => { setList(items); }, [items]);

  const add = () => {
    const v = newItem.trim();
    if (!v || list.includes(v)) return;
    setList(l => [...l, v]);
    setNewItem('');
  };

  const remove = (item) => {
    if (!window.confirm(`Remove "${item}"?`)) return;
    setList(l => l.filter(i => i !== item));
  };

  const move = (idx, dir) => {
    const next = [...list];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setList(next);
  };

  const save = async () => {
    setSaving(true);
    await onSave(list);
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <h3 style={{ fontSize: 15, marginBottom: 3 }}>{label}</h3>
          <p style={{ fontSize: 12, color: 'var(--text3)' }}>{description}</p>
        </div>
        <button className="btn-primary" style={{ fontSize: 12, padding: '7px 16px' }} onClick={save} disabled={saving}>
          {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save'}
        </button>
      </div>
      <hr className="divider" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
        {list.map((item, idx) => (
          <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '8px 12px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button onClick={() => move(idx, -1)} disabled={idx === 0} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 10, lineHeight: 1, padding: '1px 3px', cursor: 'pointer' }}>▲</button>
              <button onClick={() => move(idx, 1)} disabled={idx === list.length - 1} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 10, lineHeight: 1, padding: '1px 3px', cursor: 'pointer' }}>▼</button>
            </div>
            <span style={{ flex: 1, fontSize: 13, color: 'var(--text)' }}>{item}</span>
            <button onClick={() => remove(item)} style={{ background: 'none', border: 'none', color: 'var(--text3)', fontSize: 16, cursor: 'pointer', padding: '2px 6px', borderRadius: 4 }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--red)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text3)'}>×</button>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input value={newItem} onChange={e => setNewItem(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()}
          placeholder={`Add new ${label.toLowerCase().replace(' types', ' type')}...`} style={{ flex: 1 }} />
        <button className="btn-ghost" onClick={add}>+ Add</button>
      </div>
    </div>
  );
}

// ─── Email Template Editor ─────────────────────────────────────────────────
function EmailTemplateEditor({ customerTypes, templates, officePhone, officeEmail, onSave }) {
  const [selectedType, setSelectedType] = useState('');
  const [data, setData] = useState({});
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [newService, setNewService] = useState('');

  useEffect(() => {
    setData(templates || {});
    setPhone(officePhone || '');
    setEmail(officeEmail || '');
  }, [templates, officePhone, officeEmail]);

  useEffect(() => {
    if (customerTypes.length > 0 && !selectedType) {
      setSelectedType(customerTypes[0]);
    }
  }, [customerTypes]);

  const getTemplate = (type) => {
    const t = data[type];
    if (!t || typeof t !== 'object') return { services: [], body: '' };
    return { services: Array.isArray(t.services) ? t.services : [], body: typeof t.body === 'string' ? t.body : '' };
  };

  const current = selectedType ? getTemplate(selectedType) : { services: [], body: '' };

  const setServices = (services) => {
    if (!selectedType) return;
    setData(d => ({ ...d, [selectedType]: { ...getTemplate(selectedType), services } }));
  };

  const setBody = (body) => {
    if (!selectedType) return;
    setData(d => ({ ...d, [selectedType]: { ...getTemplate(selectedType), body } }));
  };

  const addService = () => {
    const v = newService.trim();
    if (!v || current.services.includes(v)) return;
    setServices([...current.services, v]);
    setNewService('');
  };

  const removeService = (svc) => setServices(current.services.filter(s => s !== svc));

  const save = async () => {
    setSaving(true);
    await onSave(data, phone, email);
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const VARIABLES = ['{{name}}', '{{company}}', '{{past_projects}}', '{{services}}', '{{phone}}', '{{email}}'];

  if (!selectedType) return <div style={{ color: 'var(--text3)', fontSize: 13 }}>Loading...</div>;

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <h3 style={{ fontSize: 15, marginBottom: 3 }}>Email Templates</h3>
          <p style={{ fontSize: 12, color: 'var(--text3)' }}>Customise outreach email templates per customer type.</p>
        </div>
        <button className="btn-primary" style={{ fontSize: 12, padding: '7px 16px' }} onClick={save} disabled={saving}>
          {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save All'}
        </button>
      </div>
      <hr className="divider" />

      <div className="form-row cols-2" style={{ marginBottom: 20 }}>
        <div>
          <label>Office Phone</label>
          <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="020 XXXX XXXX" />
        </div>
        <div>
          <label>Office Email</label>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="info@liongateplanningltd.co.uk" />
        </div>
      </div>

      <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>Available Variables</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {VARIABLES.map(v => (
            <span key={v} style={{ fontSize: 11, background: 'var(--accent-dim)', color: 'var(--accent)', padding: '3px 8px', borderRadius: 4, fontFamily: 'monospace', fontWeight: 600 }}>{v}</span>
          ))}
        </div>
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8, lineHeight: 1.7 }}>
          <b style={{ color: 'var(--text2)' }}>{'{{name}}'}</b> — first name &nbsp;·&nbsp;
          <b style={{ color: 'var(--text2)' }}>{'{{company}}'}</b> — " at Company" &nbsp;·&nbsp;
          <b style={{ color: 'var(--text2)' }}>{'{{past_projects}}'}</b> — auto from history &nbsp;·&nbsp;
          <b style={{ color: 'var(--text2)' }}>{'{{services}}'}</b> — services list below
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {customerTypes.map(t => (
          <button key={t} onClick={() => setSelectedType(t)} style={{
            padding: '7px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            background: selectedType === t ? 'var(--accent)' : 'var(--bg3)',
            color: selectedType === t ? '#fff' : 'var(--text2)',
            border: selectedType === t ? 'none' : '1px solid var(--border2)',
            fontFamily: 'inherit'
          }}>{t}</button>
        ))}
      </div>

      <div style={{ marginBottom: 20 }}>
        <label>Services to offer — {selectedType}</label>
        <p style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 8 }}>
          Appears as <span style={{ fontFamily: 'monospace', background: 'var(--bg3)', padding: '1px 5px', borderRadius: 3 }}>{'{{services}}'}</span> in the template.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {current.services.map(s => (
            <span key={s} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--accent-dim)', color: 'var(--accent)', fontSize: 12, padding: '4px 10px', borderRadius: 20, fontWeight: 600 }}>
              {s}
              <button onClick={() => removeService(s)} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
            </span>
          ))}
          {current.services.length === 0 && <span style={{ fontSize: 12, color: 'var(--text3)' }}>No services added yet</span>}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={newService}
            onChange={e => setNewService(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addService()}
            placeholder="e.g. Full Planning Applications"
            style={{ flex: 1 }}
          />
          <button className="btn-ghost" onClick={addService}>+ Add</button>
        </div>
      </div>

      <div>
        <label>Email Body — {selectedType}</label>
        <textarea
          rows={14}
          value={current.body}
          onChange={e => setBody(e.target.value)}
          style={{ resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.7, fontSize: 13 }}
          placeholder={`Write the email template for ${selectedType} customers...`}
        />
      </div>
    </div>
  );
}

// ─── Main Settings Page ────────────────────────────────────────────────────
export default function SettingsPage() {
  const [customerTypes, setCustomerTypes] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [taskTags, setTaskTags] = useState([]);
  const [ideaCategories, setIdeaCategories] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState({});
  const [officePhone, setOfficePhone] = useState('020 XXXX XXXX');
  const [officeEmail, setOfficeEmail] = useState('info@liongateplanningltd.co.uk');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('types');

  useEffect(() => {
    const load = async () => {
      const { data } = await supabase.from('settings').select('*');
      if (data) {
        const ct = data.find(r => r.key === 'customer_types');
        const st = data.find(r => r.key === 'service_types');
        const tt = data.find(r => r.key === 'task_tags');
        if (tt) setTaskTags(tt.value || []);
        const ic = data.find(r => r.key === 'idea_categories');
        if (ic) setIdeaCategories(ic.value || []);
        const et = data.find(r => r.key === 'email_templates');
        if (ct) setCustomerTypes(ct.value);
        if (st) setServiceTypes(st.value);
        try {
          if (et && et.value && typeof et.value === 'object') {
            const templates = {};
            Object.keys(et.value).filter(k => !k.startsWith('_')).forEach(k => {
              const v = et.value[k];
              templates[k] = typeof v === 'object' ? v : { services: [], body: typeof v === 'string' ? v : '' };
            });
            setEmailTemplates(templates);
            if (et.value._office_phone) setOfficePhone(et.value._office_phone);
            if (et.value._office_email) setOfficeEmail(et.value._office_email);
          }
        } catch(e) { console.error('Email template load error', e); }
      }
      setLoading(false);
    };
    load();
  }, []);

  const saveSetting = async (key, value) => {
    await supabase.from('settings').upsert({ key, value, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  };

  const saveEmailTemplates = async (templates, phone, email) => {
    const value = { ...templates, _office_phone: phone, _office_email: email };
    await saveSetting('email_templates', value);
    setOfficePhone(phone);
    setOfficeEmail(email);
    setEmailTemplates(templates);
  };

  if (loading) return <div style={{ padding: '28px 32px', color: 'var(--text3)' }}>Loading settings...</div>;

  const TABS = [
    { id: 'types', label: 'Types & Services' },
    { id: 'emails', label: 'Email Templates' },
    { id: 'tags', label: 'Task Tags' },
    { id: 'idea_cats', label: 'Idea Categories' },
  ];

  return (
    <div style={{ padding: '28px 32px', maxWidth: 760 }}>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 22, marginBottom: 4 }}>Settings</h1>
        <p style={{ color: 'var(--text3)', fontSize: 13 }}>Manage dropdown options and email templates.</p>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid var(--border)', paddingBottom: 0 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
            padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            background: 'transparent', border: 'none',
            color: activeTab === t.id ? 'var(--accent)' : 'var(--text3)',
            borderBottom: activeTab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
            marginBottom: -2
          }}>{t.label}</button>
        ))}
      </div>

      {activeTab === 'types' && (
        <>
          <TagEditor
            label="Customer Types"
            description="Types shown when creating or editing a customer."
            items={customerTypes}
            onSave={(list) => { setCustomerTypes(list); return saveSetting('customer_types', list); }}
          />
          <TagEditor
            label="Service Types"
            description="Service types shown when creating or editing a project."
            items={serviceTypes}
            onSave={(list) => { setServiceTypes(list); return saveSetting('service_types', list); }}
          />
        </>
      )}

      {activeTab === 'emails' && customerTypes.length > 0 && (
        <EmailTemplateEditor
          customerTypes={customerTypes}
          templates={emailTemplates || {}}
          officePhone={officePhone || ''}
          officeEmail={officeEmail || ''}
          onSave={saveEmailTemplates}
        />
      )}
      {activeTab === 'tags' && (
        <TagEditor
          label="Task Tags"
          description="Tags available when creating or editing tasks."
          items={taskTags}
          onSave={(list) => { setTaskTags(list); return saveSetting('task_tags', list); }}
        />
      )}

      {activeTab === 'idea_cats' && (
        <TagEditor
          label="Idea Categories"
          description="Categories available when creating or editing ideas."
          items={ideaCategories}
          onSave={(list) => { setIdeaCategories(list); return saveSetting('idea_categories', list); }}
        />
      )}

      {activeTab === 'emails' && customerTypes.length === 0 && (
        <div className="card" style={{ color: 'var(--text3)', fontSize: 13 }}>
          Please add Customer Types first (in the Types & Services tab) before configuring email templates.
        </div>
      )}
    </div>
  );
}
